import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { StateBase } from 'app/state/state-base';

import { FolderStructurePocService } from '../folder-structure.service';
import { FolderResourceTemplate } from '../folder-structure.types';
import { FolderTemplatesActions } from './actions';

export interface FolderTemplatesStateModel {
  templates: FolderResourceTemplate.Item[];
  selected: FolderResourceTemplate.Item | null;
}

@State<FolderTemplatesStateModel>({
  name: new StateToken<FolderTemplatesStateModel>('folderTemplatesState'),
  defaults: {
    templates: [],
    selected: null,
  },
})
@Injectable()
export class FolderTemplatesState extends StateBase {
  constructor(private readonly folderStructureService: FolderStructurePocService) {
    super();
  }

  @Selector()
  static getAll(state: FolderTemplatesStateModel): FolderResourceTemplate.Item[] {
    return state.templates;
  }

  @Selector()
  static getSelected(state: FolderTemplatesStateModel): FolderResourceTemplate.Item | null {
    return state.selected;
  }

  @Action(FolderTemplatesActions.LoadAll)
  loadAll(
    ctx: StateContext<FolderTemplatesStateModel>,
    { q }: FolderTemplatesActions.LoadAll,
  ): Observable<FolderResourceTemplate.Item[]> {
    return this.folderStructureService
      .loadAll(q)
      .pipe(tap((templates) => ctx.setState({ ...ctx.getState(), templates })));
  }

  @Action(FolderTemplatesActions.GetById)
  getById(
    ctx: StateContext<FolderTemplatesStateModel>,
    { id }: FolderTemplatesActions.GetById,
  ): Observable<FolderResourceTemplate.Item> {
    return this.folderStructureService
      .getById(id)
      .pipe(tap((selected) => ctx.setState({ ...ctx.getState(), selected })));
  }

  @Action(FolderTemplatesActions.Create)
  create(
    ctx: StateContext<FolderTemplatesStateModel>,
    { params }: FolderTemplatesActions.Create,
  ): Observable<FolderResourceTemplate.Item> {
    return this.folderStructureService.create(params).pipe(
      tap((created) => {
        const state = ctx.getState();
        ctx.setState({ ...state, templates: [...state.templates, created] });
      }),
    );
  }

  @Action(FolderTemplatesActions.Update)
  update(
    ctx: StateContext<FolderTemplatesStateModel>,
    { id, params }: FolderTemplatesActions.Update,
  ): Observable<FolderResourceTemplate.Item> {
    return this.folderStructureService.update(id, params).pipe(
      tap((updated) => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          templates: state.templates.map((t) => (t.id === updated.id ? updated : t)),
          selected: updated,
        });
      }),
    );
  }

  @Action(FolderTemplatesActions.Delete)
  delete(ctx: StateContext<FolderTemplatesStateModel>, { id }: FolderTemplatesActions.Delete): Observable<void> {
    return this.folderStructureService.delete(id).pipe(
      tap(() => {
        const state = ctx.getState();
        ctx.setState({ ...state, templates: state.templates.filter((t) => t.id !== Number(id)), selected: null });
      }),
    );
  }
}
